import React from "react";
import { useTheme } from "@mui/material/styles";
import {
  BadgeContainer,
  CustomBadge,
  BadgeAndLabelContainer,
  LabelTypography
} from "./Badge.styles";
import { SizeStyles, BadgeStyles, Variants } from "./Badge.variants";
import { adultAgeLimit } from "../../utils/constants";
import { TooltipName } from "../Tooltip/TooltipContent";
import { Tooltip } from "..";

type BadgeStylesReturnType = ReturnType<typeof BadgeStyles>;
export type BadgeStylesType = keyof BadgeStylesReturnType;

export interface CustomVariantBadgeProps {
  size?: "sm" | "md" | "lg";
  badgeStyle?: BadgeStylesType;
  showLabel?: boolean;
  withoutCircle?: boolean;
  underAgeLimit?: number;
  customTooltip?: TooltipName;
}

export const Badge = ({
  size = "sm",
  badgeStyle = "leadBooker",
  showLabel = false,
  withoutCircle = false,
  underAgeLimit,
  customTooltip,
  ...rest
}: CustomVariantBadgeProps) => {
  const theme = useTheme();

  const sizeStylesFn = SizeStyles(theme);
  const badgeStylesFn = BadgeStyles(theme);
  const VariantStylesFn = Variants(theme);

  const conditionalStyles = showLabel ? VariantStylesFn['withLabel'] : {};

  let badgeText: string;
  let labelText: string;
  let tooltip: TooltipName;
  switch (badgeStyle) {
    case "leadBooker":
      badgeText = "LB";
      tooltip = "leadBooker";
      labelText = "Lead Booker";
      break;
    case "underAge":
      badgeText = `-${underAgeLimit ?? adultAgeLimit}`;
      tooltip = "ageLimit";
      labelText = "Age Limit";
      break;
    case "underMinimumAge":
      badgeText = `UMA`;
      tooltip = "underAgeLimit";
      labelText = "Under Age Limit";
      break;
    case "generalAdmission":
      badgeText = "GA";
      tooltip = "generalAdmission";
      labelText = "General Admission";
      break;
    case "accessibilityRequired":
      badgeText = "AR";
      tooltip = "accessibilityRequired";
      labelText = "Accessibility Required";
      break;
    case "vip":
      badgeText = "VIP";
      tooltip = "vip";
      labelText = "VIP";
      break;
    case "friendsAndFamily":
      badgeText = "F&F";
      tooltip = "friendsAndFamily";
      labelText = "Friends & Family";
      break;
    case "generic":
      badgeText = "GEN";
      tooltip = "generic";
      labelText = "Generic";
      break;
    case "groupTicket":
      badgeText = "GT";
      tooltip = "groupTicket";
      labelText = "Group Ticket";
      break;
    case "priority":
      badgeText = "PRI";
      tooltip = "priority";
      labelText = "Priority";
      break;
    case "prioritySparkles":
      badgeText = "✨";
      tooltip = "priority";
      labelText = "Priority";
      break;
  }

  return (
    <Tooltip tooltipName={customTooltip ? customTooltip : tooltip}>
      <BadgeAndLabelContainer sx={{ ...conditionalStyles }}>
        {showLabel && <LabelTypography>{labelText}</LabelTypography>}
        <BadgeContainer>
          <CustomBadge
            sx={{
              ...sizeStylesFn[size],
              ...badgeStylesFn[badgeStyle],
              border: withoutCircle ? 'none' : null,
            }}
            {...rest}
          >
            {badgeText}
          </CustomBadge>
        </BadgeContainer>
      </BadgeAndLabelContainer>
    </Tooltip>
  );
};

export interface BadgeProps extends React.ComponentProps<typeof Badge> {
  as?: React.ElementType;
}

Badge.displayName = "Badge";